import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/base/JsonLd';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'romance');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const category = getCategoryBySlug(activeCategory);
  const comics = getComicsByCategory(activeCategory);

  return (
    <>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tiantianmanhua.com' },
            { '@type': 'ListItem', position: 2, name: `${category?.name || '分类'}漫画`, item: `https://www.tiantianmanhua.com/category/${activeCategory}` },
          ],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: `${category?.name || '分类'}漫画 - 天天漫画网`,
          description: `浏览所有${category?.name || ''}类韩国漫画，下拉式完整版免费阅读，天天漫画网`,
          url: `https://www.tiantianmanhua.com/category/${activeCategory}`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tiantianmanhua.com' },
              { '@type': 'ListItem', position: 2, name: `${category?.name || '分类'}漫画` },
            ],
          },
        }}
      />
      <Header />
      <main className="min-h-screen pt-16">
        {/* Category Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-950 font-medium">{category?.name || '分类'}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              {category?.name || '分类'}漫画
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              浏览所有{category?.name || '分类'}类韩国漫画，下拉式完整版免费阅读
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 mt-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 bg-background-100 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Comic Grid */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 mt-8 mb-16">
          {comics.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-600">
                  共 <strong className="text-foreground-950">{comics.length}</strong> 部{category?.name || ''}漫画
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {comics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
              <Link
                to="/"
                className="inline-block mt-4 px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}